import { type ChangeEvent, useId } from "react";

import { type OptionType } from "ui/select/select.types";
import { formatDisplayedRows, getPageCount } from "ui/table-pagination/table-pagination.helpers";
import { type TablePaginationProps } from "ui/table-pagination/table-pagination.types";

type UseTablePaginationArgs = Required<
  Pick<TablePaginationProps, "count" | "page" | "rowsPerPage" | "onPageChange">
> &
  Pick<TablePaginationProps, "onRowsPerPageChange" | "rowsPerPageOptions">;

export const useTablePagination = ({
  count,
  page,
  rowsPerPage,
  rowsPerPageOptions = [],
  onPageChange,
  onRowsPerPageChange,
}: UseTablePaginationArgs) => {
  // The select's name is also its `id`, so it has to be unique among the tables on a page.
  const pageSizeSelectName = `rows-per-page-${useId()}`;
  const pageSizeOptions: OptionType[] = rowsPerPageOptions.map((size) => ({
    value: size,
    label: String(size),
  }));

  // MUI's `Pagination` counts pages from 1; the table's contract, like MUI's `TablePagination`, from 0.
  const handlePageChange = (_event: ChangeEvent<unknown>, oneBasedPage: number) => {
    onPageChange(oneBasedPage - 1);
  };

  const handleRowsPerPageChange = (option: OptionType | null) => {
    if (option) {
      onRowsPerPageChange?.(Number(option.value));
    }
  };

  return {
    pageSizeSelectName,
    pageCount: getPageCount(count, rowsPerPage),
    displayedRows: formatDisplayedRows(page, rowsPerPage, count),
    pageSizeOptions,
    selectedPageSize: { value: rowsPerPage, label: String(rowsPerPage) },
    hasPageSizeSelect: Boolean(onRowsPerPageChange),
    handlePageChange,
    handleRowsPerPageChange,
  };
};
